import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../components/ui/Modal.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import api from '../../lib/api.js'

/*
 * Create/edit an admin user — POST/PUT /admin/users. Multi-company assignment
 * (company_ids[], ≥1 required on create). Roles are managed per-company via the
 * company details modal, not here (info banner). Password required on create.
 */
const LANGS = ['fr', 'en', 'nl']

export default function AdminUserFormModal({ user, companies, onClose, onSaved }) {
  const { t } = useTranslation()
  const isEdit = !!user
  const [f, setF] = useState({
    email: user?.email || '', first_name: user?.first_name || '', last_name: user?.last_name || '',
    company_ids: user?.company_ids || [], preferred_language: user?.preferred_language || 'fr', password: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [companyQuery, setCompanyQuery] = useState('')
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const toggleCompany = (id) => set('company_ids', f.company_ids.includes(id) ? f.company_ids.filter((x) => x !== id) : [...f.company_ids, id])

  useEffect(() => { setError('') }, [f])

  const submit = async (e) => {
    e.preventDefault()
    if (!isEdit && f.company_ids.length === 0) { setError(t('admin.users.companyRequired')); return }
    if (!isEdit && !f.password) { setError(t('admin.users.passwordRequired')); return }
    setSaving(true)
    // Roles are managed per-company elsewhere (company details modal), NOT here.
    // On EDIT we must NOT send `roles` at all — sending it (even empty) makes the
    // backend rewrite the user's roles and would drop existing ones (super-admin
    // included). On CREATE we send an explicit empty list so the backend doesn't
    // fall back to its invalid "utilisateur" default.
    const payload = { email: f.email, first_name: f.first_name, last_name: f.last_name, company_ids: f.company_ids, preferred_language: f.preferred_language }
    if (!isEdit) payload.roles = []
    if (f.password) payload.password = f.password
    try {
      if (isEdit) await api.put(`/admin/users/${user.id}`, payload)
      else await api.post('/admin/users/', payload)
      onSaved()
    } catch (e2) { setError(e2?.response?.data?.error || t('admin.errorGeneric')) } finally { setSaving(false) }
  }

  const filtered = companies.filter((c) => !companyQuery || (c.name || '').toLowerCase().includes(companyQuery.toLowerCase()))

  return (
    <Modal open onClose={onClose} maxWidth={560} closeOnBackdrop={false}>
      <form onSubmit={submit} style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>{isEdit ? t('admin.users.edit') : t('admin.users.new')}</h3>
        {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label={t('settings.profile.firstName')} required><Input value={f.first_name} onChange={(e) => set('first_name', e.target.value)} required /></Field>
          <Field label={t('settings.profile.lastName')} required><Input value={f.last_name} onChange={(e) => set('last_name', e.target.value)} required /></Field>
        </div>
        <Field label="Email" required><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} required /></Field>
        <Field label={isEdit ? t('admin.users.passwordOptional') : t('auth.login.password')} required={!isEdit}>
          <Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => set('password', e.target.value)} placeholder={isEdit ? t('admin.users.passwordKeep') : undefined} />
        </Field>
        <Field label={t('parties.fields.language')}>
          <div style={{ display: 'flex', gap: 8 }}>{LANGS.map((l) => (
            <button key={l} type="button" onClick={() => set('preferred_language', l)} style={{ flex: 1, height: 40, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600, border: '1.5px solid ' + (f.preferred_language === l ? 'var(--color-primary)' : 'var(--color-border-strong)'), background: f.preferred_language === l ? 'var(--color-bg-lilac-strong)' : '#fff', color: f.preferred_language === l ? 'var(--color-primary)' : 'var(--color-text)' }}>{l.toUpperCase()}</button>
          ))}</div>
        </Field>

        <Field label={t('admin.users.companies')} hint={t('admin.users.rolesHint')} required={!isEdit}>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={15} /></span>
            <Input value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)} placeholder={t('admin.companies.search')} style={{ height: 38, paddingLeft: 34 }} />
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 10 }}>
            {filtered.map((c) => {
              const on = f.company_ids.includes(c.id)
              return (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderTop: '1px solid var(--color-border-row)', cursor: 'pointer', background: on ? 'var(--color-bg-lilac)' : '#fff' }}>
                  <input type="checkbox" checked={on} onChange={() => toggleCompany(c.id)} />
                  <span style={{ fontSize: 13.5, color: 'var(--color-ink)' }}>{c.name}</span>
                  {c.vat_number && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>· {c.vat_number}</span>}
                </label>
              )
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 5 }}>{t('admin.users.selected', { count: f.company_ids.length })}</div>
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="check" loading={saving}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  )
}
